import React from 'react';
import ReactDOM from 'react-dom/client';
// First, so the phone-system link is saved before any request can 401 and leave the page.
import './lib/customerCallLink';
import { QueryClientProvider } from '@tanstack/react-query';
import { appQueryClient as queryClient } from './lib/queryClient';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { getClient } from '@matix/api-client';
import { App } from './App';
import { LanguageProvider } from './lib/language';
import { bootClient } from './lib/client';
import { isSessionReady, getAuthToken, STORAGE_KEY } from './lib/cashierAuthClient';
import './styles/pos.css';
// Shared sign-in ticket. Namespaced `.tkt-*`, so it sits alongside pos.css
// without touching the till's own styles.
import '@matix/ui/login-styles';
import '@matix/ui/setup-styles';
import { installNativeBridge, hideNativeSplash, lockSessionOnColdStart } from '@matix/ui';

// The PWA/service worker was removed (it required a valid cert and kept
// serving a stale cached bundle after each deploy). Actively unregister any
// worker a previous build installed and wipe its caches — this is a local
// operation (no sw.js fetch), so it works even on a site with a bad/missing
// certificate. Once done, the app loads straight from the server every time.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations()
    .then((regs) => regs.forEach((r) => void r.unregister()))
    .catch(() => { /* ignore */ });
}
if (typeof caches !== 'undefined' && caches.keys) {
  caches.keys()
    .then((keys) => keys.forEach((k) => void caches.delete(k)))
    .catch(() => { /* ignore */ });
}

// Inside the Matix POS phone app only (a no-op in a browser): routes the
// Android back button, themes the status bar and turns off pinch-zoom.
// With biometrics on, a COLD start puts the saved session away so the till
// opens on sign-in and asks for the fingerprint / face first. Must run
// BEFORE anything reads the session below. A no-op in a browser.
lockSessionOnColdStart(STORAGE_KEY);
installNativeBridge();

bootClient();

// On reload, re-seed the shared api-client with a previously-stored cashier
// session token BEFORE React mounts, so the AuthProvider that wraps
// LegacyApp (App.tsx's 'session' branch) reads a valid token at mount —
// no second login, no race. Fresh logins set the token in the auth store.
if (isSessionReady()) {
  const token = getAuthToken();
  if (token) getClient().setToken(token);
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <App />
      </LanguageProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </React.StrictMode>,
);

// The shell shows a splash until the till has painted.
hideNativeSplash();
